import { useS4LContext } from '@s4l/react-context'
import { useState, useEffect, useMemo } from 'react'

export enum BreakpointSizes {
  XS = 0,
  S = 320,
  M = 768,
  L = 1024,
}

export enum Devices {
  Desktop = 'desktop',
  Mobile = 'mobile',
  Tablet = 'tablet',
}

// Browser/OS detection utilities
const detectIsIos = (): boolean => {
  if (typeof window === 'undefined') return false

  const userAgent = window.navigator.userAgent.toLowerCase()

  return (
    /iphone|ipad|ipod/.test(userAgent) || (userAgent.includes('mac') && 'ontouchend' in document)
  )
}

const detectIsSafari = (): boolean => {
  if (typeof window === 'undefined') return false
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

const detectIsAppleDevice = (): boolean => {
  return detectIsIos() || detectIsSafari()
}

const detectIsApp = (): boolean => {
  return /sfl-mobile-app/i.test(navigator.userAgent)
}

export const useBrowser = () => {
  const { device: initialDevice } = useS4LContext()

  const initialDeviceMapper = {
    mobile: BreakpointSizes.S,
    tablet: BreakpointSizes.M,
    desktop: BreakpointSizes.L,
  }[initialDevice]

  const [activeBreakpoint, setActiveBreakpoint] = useState(initialDeviceMapper ?? BreakpointSizes.S)

  const device = useMemo(() => {
    if (activeBreakpoint <= BreakpointSizes.S) return Devices.Mobile
    if (activeBreakpoint <= BreakpointSizes.M) return Devices.Tablet
    return Devices.Desktop
  }, [activeBreakpoint])

  useEffect(() => {
    const mediaQueries = {
      mobile: window.matchMedia(`(max-width: ${BreakpointSizes.M - 1}px)`),
      tablet: window.matchMedia(
        `(min-width: ${BreakpointSizes.M}px) and (max-width: ${BreakpointSizes.L - 1}px)`
      ),
      desktop: window.matchMedia(`(min-width: ${BreakpointSizes.L}px)`),
    }

    const handleMediaQuery = (e: MediaQueryListEvent | MediaQueryList) => {
      if (!e.matches) return

      let breakpoint: BreakpointSizes

      if (e === mediaQueries.mobile || e.media === mediaQueries.mobile.media) {
        breakpoint = BreakpointSizes.S
      } else if (e === mediaQueries.tablet || e.media === mediaQueries.tablet.media) {
        breakpoint = BreakpointSizes.M
      } else {
        breakpoint = BreakpointSizes.L
      }

      setActiveBreakpoint(breakpoint)
    }

    Object.values(mediaQueries).forEach(mq => {
      mq.addEventListener('change', handleMediaQuery)
      handleMediaQuery(mq)
    })

    return () => {
      Object.values(mediaQueries).forEach(mq => {
        mq.removeEventListener('change', handleMediaQuery)
      })
    }
  }, [])

  const isAppleDevice = useMemo(() => detectIsAppleDevice(), [])
  const isApp = useMemo(() => detectIsApp(), [])

  return {
    activeBreakpoint,
    device,
    isAppleDevice,
    isIOSApp: isApp && isAppleDevice,
    isAndroidApp: isApp && !isAppleDevice,
  }
}
